import React, { ReactNode, useEffect, useMemo } from 'react';
import { useNotifications } from '@/hooks/use-notifications.ts';
import { usePostSubscribePushNotifications } from '@/features/notifications/hooks/mutations/use-post-subscribe-push-notifications.ts';
import { usePushNotificationStore } from '@/services/store/use-push-notification-store.ts';
import { usePostUpdateStatusPushNotifications } from '@/features/notifications/hooks/mutations/use-post-update-status-push-notification.ts';
import { useShallow } from 'zustand/react/shallow';
import { PushNotificationsContext } from './use-push-notifications-context.ts';

interface NotificationsProviderProps {
  children: ReactNode;
}

function hasPassed7Days(specificDate: number): boolean {
  const currentDate = new Date();
  const dateToCompare = new Date(specificDate);

  const timeDifference = currentDate.getTime() - dateToCompare.getTime();
  const daysDifference = timeDifference / (1000 * 60 * 60 * 24);

  return daysDifference >= 7;
}

export const PushNotificationsProvider: React.FC<NotificationsProviderProps> = ({
  children,
}) => {
  const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;

  const {
    arePushNotificationsAllowed,
    lastSubscriptionStatusSentAt,
    setLastSubscriptionStatusSentAt,
    setArePushNotificationsAllowed,
  } = usePushNotificationStore(
    useShallow((state) => ({
      arePushNotificationsAllowed: state.arePushNotificationsAllowed,
      lastSubscriptionStatusSentAt: state.lastSubscriptionStatusSentAt,
      setLastSubscriptionStatusSentAt: state.setLastSubscriptionStatusSentAt,
      setArePushNotificationsAllowed: state.setArePushNotificationsAllowed,
    }))
  );

  const { mutate: postSubscribePushNotifications } = usePostSubscribePushNotifications({
    onSuccess: () => setLastSubscriptionStatusSentAt(new Date().getTime()),
  });

  const { mutate: postUpdatePushNotificationsStatus } = usePostUpdateStatusPushNotifications({
    onSuccess: () => setLastSubscriptionStatusSentAt(new Date().getTime()),
  });

  const notifications = useNotifications({
    vapidPublicKey,
    onSubscriptionChange: async (subscription) => {
      if (subscription) {
        const subscriptionJson = subscription.toJSON();

        setArePushNotificationsAllowed(true);
        postSubscribePushNotifications({
          endpoint: subscription.endpoint,
          expirationTime: subscription.expirationTime,
          p256dhKey: subscriptionJson.keys?.p256dh,
          authKey: subscriptionJson.keys?.auth,
        });
      }
    },
  });

  useEffect(() => {
    if (!notifications.subscriptionDetails?.authKey) return;

    if (!lastSubscriptionStatusSentAt) {
      setLastSubscriptionStatusSentAt(new Date().getTime());
      return;
    }

    if (hasPassed7Days(lastSubscriptionStatusSentAt)) {
      postUpdatePushNotificationsStatus({
        authKey: notifications.subscriptionDetails?.authKey,
      });
    }
  }, [notifications.subscriptionDetails?.authKey]);

  const contextValue = useMemo(
    () => ({
      isSubscribed: notifications.isSubscribed,
      isLoading: notifications.isLoading,
      status: notifications.status,
      subscribe: notifications.subscribe,
      unsubscribe: notifications.unsubscribe,
      clearStatus: notifications.clearStatus,
      arePushNotificationsAllowed,
      setArePushNotificationsAllowed,
    }),
    [
      notifications.isSubscribed,
      notifications.isLoading,
      notifications.status,
      notifications.subscribe,
      notifications.unsubscribe,
      notifications.clearStatus,
      arePushNotificationsAllowed,
      setArePushNotificationsAllowed,
    ]
  );

  return (
    <PushNotificationsContext.Provider value={contextValue}>
      {children}
    </PushNotificationsContext.Provider>
  );
};
